import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { getProductsForCustomer, setCustomPricing } from "../api/productApi";

interface SetPricingModalProps {
  customerId: number; // For single-customer support
  customerIds?: number[]; // Optional for multi-customer support
  customerName: string;
  open: boolean;
  onClose: () => void;
}

interface ProductVariant {
  id: number;
  variant_name: string;
  price: number;
  custom_price?: number;
}

interface Product {
  product_id: number;
  name: string;
  base_price: number | null;
  custom_price?: number;
  variants?: ProductVariant[];
}

const SetPricingModal: React.FC<SetPricingModalProps> = ({
  customerId,
  customerIds,
  customerName,
  open,
  onClose,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [bulkDiscount, setBulkDiscount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [activeTab, setActiveTab] = useState<number>(0); // 0: Standard View, 1: Spreadsheet View

  const handleSnackbarClose = () => setSnackbarOpen(false);

  useEffect(() => {
    if (open) {
      fetchCustomerProducts();
    }
  }, [open]);

  const fetchCustomerProducts = async (): Promise<void> => {
    try {
      setLoading(true);
      const rawProducts = customerIds
        ? await Promise.all(customerIds.map((id) => getProductsForCustomer(id)))
        : await getProductsForCustomer(customerId);
      const mergedProducts = customerIds
        ? ([] as Product[]).concat(...rawProducts)
        : rawProducts;
      setProducts(mergedProducts);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch products for customer(s):", error);
      setLoading(false);
    }
  };

  const handleBulkDiscountChange = (value: string) => {
    setBulkDiscount(parseFloat(value) || 0);
  };

  const handlePriceChange = (
    productIndex: number,
    value: string,
    variantIndex?: number
  ) => {
    const updatedProducts = [...products];
    if (variantIndex !== undefined) {
      updatedProducts[productIndex].variants![variantIndex].custom_price =
        parseFloat(value) || 0;
    } else {
      updatedProducts[productIndex].custom_price = parseFloat(value) || 0;
    }
    setProducts(updatedProducts);
  };

  const handleProductSelect = (productId: number) => {
    const updatedSelection = new Set(selectedProducts);
    if (updatedSelection.has(productId)) {
      updatedSelection.delete(productId);
    } else {
      updatedSelection.add(productId);
    }
    setSelectedProducts(updatedSelection);
  };

  const applyBulkDiscount = () => {
    if (bulkDiscount <= 0 || selectedProducts.size === 0) {
      setSnackbarMessage("Please enter a valid discount and select products.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const updatedProducts = products.map((product) => {
      if (selectedProducts.has(product.product_id)) {
        if (product.base_price !== null) {
          const discountedPrice =
            product.base_price - (product.base_price * bulkDiscount) / 100;
          product.custom_price = parseFloat(discountedPrice.toFixed(2));
        }

        if (product.variants?.length) {
          product.variants = product.variants.map((variant) => {
            const variantDiscountedPrice =
              variant.price - (variant.price * bulkDiscount) / 100;

            return {
              ...variant,
              custom_price: parseFloat(variantDiscountedPrice.toFixed(2)),
            };
          });
        }
      }
      return product;
    });

    setProducts(updatedProducts);
    setBulkDiscount(0);
    setSnackbarMessage("Discount applied successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleSavePricing = async () => {
    try {
      for (const product of products) {
        if (product.custom_price !== undefined) {
          await setCustomPricing({
            customerId,
            productId: product.product_id,
            customPrice: product.custom_price,
          });
        }

        if (product.variants?.length) {
          for (const variant of product.variants) {
            if (variant.custom_price !== undefined) {
              await setCustomPricing({
                customerId,
                productId: product.product_id,
                variantId: variant.id,
                customPrice: variant.custom_price,
              });
            }
          }
        }
      }

      setSnackbarMessage("Prices saved successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Failed to save custom pricing", error);
      setSnackbarMessage("Failed to save prices.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Set Pricing for {customerName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label="Standard View" />
          <Tab label="Spreadsheet View" />
        </Tabs>
        {activeTab === 0 && (
          <Box>
            <Box mb={3}>
              <Typography variant="subtitle1">Bulk Discount</Typography>
              <TextField
                type="number"
                value={bulkDiscount}
                onChange={(e) => handleBulkDiscountChange(e.target.value)}
                label="Discount %"
                fullWidth
              />
              <Button
                variant="contained"
                color="primary"
                onClick={applyBulkDiscount}
                sx={{ mt: 2 }}
              >
                Apply Discount
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedProducts.size > 0 &&
                          selectedProducts.size < products.length
                        }
                        checked={selectedProducts.size === products.length}
                        onChange={(e) =>
                          setSelectedProducts(
                            e.target.checked
                              ? new Set(products.map((product) => product.product_id))
                              : new Set()
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Base Price</TableCell>
                    <TableCell align="right">Custom Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product, index) => (
                    <React.Fragment key={product.product_id}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedProducts.has(product.product_id)}
                            onChange={() => handleProductSelect(product.product_id)}
                          />
                        </TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">
                          {product.base_price !== null ? `$${product.base_price}` : "N/A"}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={product.custom_price || ""}
                            onChange={(e) => handlePriceChange(index, e.target.value)}
                            type="number"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      {product.variants?.length && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Variants</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Variant</TableCell>
                                      <TableCell align="right">Base Price</TableCell>
                                      <TableCell align="right">Custom Price</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {product.variants.map((variant, vIndex) => (
                                      <TableRow key={variant.id}>
                                        <TableCell>{variant.variant_name}</TableCell>
                                        <TableCell align="right">
                                          {variant.price
                                            ? `$${variant.price.toFixed(2)}`
                                            : "N/A"}
                                        </TableCell>
                                        <TableCell align="right">
                                          <TextField
                                            value={variant.custom_price || ""}
                                            onChange={(e) =>
                                              handlePriceChange(index, e.target.value, vIndex)
                                            }
                                            type="number"
                                            size="small"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        {activeTab === 1 && (
          <Typography>Spreadsheet view coming soon...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSavePricing}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Save
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SetPricingModal;
